import type {ObjectDirective} from 'vue';
import type Swiper from "swiper";
import type {SwiperOptions} from "swiper";
import {Deferred} from "@/serviceWorker/workerComponents/Utilities/Deferred";
import {getRootElement, isElementVisible} from "@/utilities/domUtils";
import type {ComponentPublicInstance, DirectiveHook, RendererElement, RendererNode, VNode} from "@vue/runtime-core";
import {asyncDelay, evaluateResultWithContext} from "@/utilities/nonDomUtils";
import {isEqual} from "lodash-es";
import {getEnv} from "@/utilities/env";

const swiperBaseUrl = getEnv('VITE_NF_BASE_PATH') + 'vendor/swiper/';
let swiperImportState:null|'importing'|'loaded' = null;
const swiperDeferred = new Deferred<Swiper>();
let swiper: Swiper | null = null;
let stylesLoaded = false;

declare type DirectiveModifiers = Record<string, boolean>;
interface SwiperDirectiveBinding<V=any>  {
    instance: ComponentPublicInstance | null;
    value: V;
    oldValue: V | null;
    arg?: string;
    modifiers: DirectiveModifiers;
    dir: SwiperDirective<any, V>;
}
declare type NfProps = {
    mountedSwiper?: Swiper,
    intersectionObserver?: IntersectionObserver,
    waitingForEnabledInterval?: number,
    'swiper-images'?: string,
    swiperTurnedOn?: boolean,
    renderedImages?: string[]
};
declare type swiperVNode = VNode< RendererNode,  RendererElement> & {nfprops: NfProps
};
interface SwiperDirective <T = any, V = any> extends Omit<ObjectDirective<T, V>, 'mounted'|'created'|'beforeUnmount'|'updated'> {
    onElementVisible: (context:any,nfprops: NfProps, props: swiperVNode['props'], entries: IntersectionObserverEntry[], observer: IntersectionObserver)=> void,
    mounted: (this:void,el: T, binding: SwiperDirectiveBinding<V>, vnode: swiperVNode, prevVNode: VNode<any, T> | null) => void,
    updated: (this:void,el: T, binding: SwiperDirectiveBinding<V>, vnode: swiperVNode, prevVNode: swiperVNode ) => void,
    created: (this:void,el: T, binding: SwiperDirectiveBinding<V>, vnode: swiperVNode, prevVNode: VNode<any, T> | null) => void,
    beforeUnmount: (this:void,el: T, binding: SwiperDirectiveBinding<V>, vnode: swiperVNode, prevVNode: VNode<any, T> | null) => void,
    loadImages: (element:Element, context: any,nfprops: NfProps, props: swiperVNode['props']) => void,
    onImageLoaded: (element: Element) => void,
    initializeSwiper: (el: T, binding: SwiperDirectiveBinding<V>, vnode: swiperVNode) => void,
    copyOldProps: (node: swiperVNode, prevVNode: swiperVNode) => void,
    getImageUrls: (element:Element, context: any) => string[],
    createSlideElement: (swiperDirectiveElement: Element, imageUrl: string, imageIndex: number, lazyLoad: boolean) => HTMLElement,
    reloadImages: (element:Element, context: any, nfprops: NfProps, props: swiperVNode['props']) => void
}
/**
 * Dyrektywa ładująca swipera na obazkach
 * Zacznie ładować swipera dopiero gdy faktycznie zostanie użyta
 * Uwaga z tymi dyrektywami, istnieje tylko jedna instancja dyrektywy na cały system, dodatkowe properki per element można w vnode umieścić
 */
export default {
    created(el, binding, vnode, prevVnode) {
        if(swiperDeferred.isResolved()) {
            swiperImportState = 'loaded';
        } else if(swiperImportState === null) {
            swiperImportState = 'importing';
            import(swiperBaseUrl+'swiper-bundle.esm.browser.min.js').then(swiperModule => {
                swiper = swiperModule.Swiper;
                swiperImportState = 'loaded';
                swiperDeferred.resolve(swiper);
            });

        }
    },
    initializeSwiper(el: Element, binding: SwiperDirectiveBinding<any>, node:swiperVNode) {
        if(!node.nfprops) {
            node.nfprops = {};
        }
        //region czyszczenie ponownie użytego htmlu

        const swiperWrapper = el.querySelector('.swiper-wrapper');
        if(swiperWrapper) {
            swiperWrapper.innerHTML = '';
        }
        el.classList.remove('swiper-images-loaded');
        el.classList.remove('swiper-initialized');
        //endregion
        node.nfprops.intersectionObserver = new IntersectionObserver(
            binding.dir.onElementVisible.bind(binding.dir, binding.instance, node.nfprops, node.props),
            {
                threshold: 0.1,
                // @ts-ignore
                delay: 100
            });
        node.nfprops.intersectionObserver.observe(el);
        window.setTimeout(() => {
            if(!node.nfprops) {
                node.nfprops = {};
            }
            node.nfprops.swiperTurnedOn = isElementVisible(el as HTMLElement);
            swiperDeferred.promise.then(async (Swiper) => {
                // @ts-ignore
                // @ts-ignore
                const options:SwiperOptions = {
                    init: node.nfprops?.swiperTurnedOn ?? false,
                    preloadImages: true,
                    loop: true,
                    loopPreventsSlide: false,
                    slidesPerView: 1,
                    enabled: false,
                    navigation: {
                        //@ts-ignore
                        nextEl: el.querySelector('.swiper-button-next'),
                        //@ts-ignore
                        prevEl: el.querySelector('.swiper-button-prev'),
                        enabled: true
                    },
                    initialSlide: 0,
                    lazy: {
                        loadPrevNext: true,
                        enabled: true,
                        loadOnTransitionStart: true
                    },
                    on: {
                        imagesReady: binding.dir.onImageLoaded.bind(binding.dir, el)
                    }
                }
                if(binding.value) {
                    Object.assign(options,binding.value);
                }
                if(!node.nfprops) {
                    node.nfprops = {};
                }
                if(node.nfprops?.swiperTurnedOn) {
                    binding.dir.loadImages(el,binding.instance,node.nfprops, node.props);
                }
                // @ts-ignore
                node.nfprops.mountedSwiper = new Swiper(el, options);
                if(node.nfprops.mountedSwiper) {
                    node.nfprops.mountedSwiper.realIndex = 0;
                    node.nfprops.mountedSwiper.enable();
                }

                if(!node.nfprops?.swiperTurnedOn) {
                    let moreRetries = 5;
                    while (!node.nfprops?.swiperTurnedOn && moreRetries--) {
                        await asyncDelay(100);
                        if(isElementVisible(el as HTMLElement)) {
                            // @ts-ignore
                            binding.dir.onElementVisible(binding.instance, node.nfprops, node.props,[{
                                isIntersecting: true,
                                target: el
                            }], null);
                        }
                    }
                }
            });

        });

    },
    mounted(el, binding, node) {
        if(!stylesLoaded) {
            const linkElement = document.createElement('link');
            linkElement.rel   = 'stylesheet';
            linkElement.href  = swiperBaseUrl+'swiper-bundle.min.css';
            getRootElement(el).appendChild(linkElement);
            stylesLoaded = true;
        }
        if(!node.nfprops) {
            node.nfprops = {};
        }
        if(binding.value?.enabled === false) {
            node.nfprops.waitingForEnabledInterval = window.setInterval(() => {
                binding.instance?.$forceUpdate();
            },100);
            return;
        } else {
            binding.dir.initializeSwiper(el, binding, node);
        }
    },
    copyOldProps(node: swiperVNode, prevVNode: swiperVNode) {
        if (!node.nfprops && prevVNode.nfprops) {
            node.nfprops = prevVNode.nfprops;
        }
        if (prevVNode.nfprops && node.nfprops) {
            for (const key in prevVNode.nfprops) {
                // @ts-ignore
                if (!node.nfprops[key]) {
                    // @ts-ignore
                    node.nfprops[key] = prevVNode.nfprops[key];
                }
            }
        }
    },
    updated(el, binding, node, prevVNode) {
        binding.dir.copyOldProps(node, prevVNode);
        if(node.nfprops?.waitingForEnabledInterval) {
            if(!binding.value?.enabled) {
                return;
            } else {
                let interval = null;
                if(node.nfprops?.waitingForEnabledInterval) {
                    interval = node.nfprops?.waitingForEnabledInterval;
                    delete node.nfprops.waitingForEnabledInterval;
                    window.clearInterval(interval);
                }
                binding.dir.initializeSwiper(el, binding, node);
            }
            return;
        }
        if(node.nfprops?.swiperTurnedOn && node.nfprops.mountedSwiper) {
            const images = binding.dir.getImageUrls(el, binding.instance);
            if(!isEqual(node.nfprops.renderedImages, images)) {
                binding.dir.reloadImages(el, binding.instance, node.nfprops, node.props);
            }
        }
    },
    onImageLoaded(el: Element) {
        el.classList.add('swiper-images-loaded');
    },
    onElementVisible(context:any,nfprops: NfProps, props, entries, observer) {
        if(nfprops && !nfprops.swiperTurnedOn && nfprops.mountedSwiper && entries.length && entries[0].isIntersecting) {
            this.loadImages(entries[0].target, context, nfprops, props);
            // @ts-ignore
            nfprops.mountedSwiper.init(entries[0].target);
            nfprops.mountedSwiper.realIndex = 0;
            nfprops.mountedSwiper.enable();
            nfprops.swiperTurnedOn = true;
        }
    },
    loadImages(element:Element, context: any, nfprops: NfProps, props) {
        if(props?.["swiper-images"]) {
            let images:null|string[] = null;
            const swiperWrapper = element.querySelector('.swiper-wrapper');
            if(swiperWrapper) {
                swiperWrapper.innerHTML = '';
                images = this.getImageUrls(element, context);

                if (images && Array.isArray(images)) {
                    let imageUrl: string;
                    let imageIndex = 0;
                    // @ts-ignore
                    for (imageUrl of images) {
                        swiperWrapper.appendChild(this.createSlideElement(element, imageUrl, imageIndex, true));
                    }
                    nfprops.renderedImages = images;
                }
            } else {
                console.error('Brakuje elementu .swiper-wrapper');
            }
        }
    },
    getImageUrls(element:Element, context: any): string[] {
        return evaluateResultWithContext(element.getAttribute('swiper-images'), context);
    },
    reloadImages(element:Element, context: any, nfprops: NfProps, props: swiperVNode['props']) {
        if(props?.["swiper-images"]) {
            let images:null|string[] = null;
            const swiper = nfprops.mountedSwiper;
            if(swiper) {
                images = this.getImageUrls(element, context);

                if (images && Array.isArray(images)) {
                    let imageUrl: string;
                    let imageIndex = 0;
                    swiper.removeAllSlides();
                    const slidesToLoad = [];
                    // @ts-ignore
                    for (imageUrl of images) {
                        slidesToLoad.push(this.createSlideElement(element, imageUrl, imageIndex, true));
                    }
                    swiper.appendSlide(slidesToLoad);
                    nfprops.renderedImages = images;
                    swiper.slideTo(1);
                    swiper.lazy.load();
                    window.setTimeout(function() {
                        swiper.update();
                    })
                }
            } else {
                console.error('Brakuje zamontowanego swipera');
            }
        }
    },
    createSlideElement(swiperDirectiveElement: Element, imageUrl: string, imageIndex: number, lazyLoad: boolean = true): HTMLElement {
        const slideElement = document.createElement('div');
        slideElement.classList.add('swiper-slide');
        slideElement.setAttribute('data-swiper-slide-index',String(imageIndex++));
        const imageElement = document.createElement('img');
        if (lazyLoad) {
            slideElement.innerHTML = '<div class="swiper-lazy-preloader lds-dual-ring"></div>';
            imageElement.dataset['src'] = getEnv('VITE_IMAGES_HOST') + "/w500-c/" + imageUrl;
        } else {
            imageElement.src = getEnv('VITE_IMAGES_HOST') + "/w500-c/" + imageUrl;
        }
        imageElement.classList.add('swiper-lazy');
        imageElement.onload = this.onImageLoaded.bind(this, swiperDirectiveElement);
        slideElement.appendChild(imageElement);
        return slideElement;
    },

    beforeUnmount( element, binding,node) {
        if(!node.nfprops) {
            node.nfprops = {};
        }
        if(node.nfprops.mountedSwiper) {
            try {
                node.nfprops.mountedSwiper.disable();
                node.nfprops.mountedSwiper.destroy();
            } catch(e) {
                //ignore
            }
            delete node.nfprops.mountedSwiper;
        }
        if(node.nfprops.intersectionObserver) {
            node.nfprops.intersectionObserver.unobserve(element);
            node.nfprops.intersectionObserver.disconnect();
            delete node.nfprops.intersectionObserver;
        }
        const swiperWrapper = element.querySelector('.swiper-wrapper');
        if(swiperWrapper) {
            swiperWrapper.innerHTML = '';
        }
        element.classList.remove('swiper-images-loaded');
        element.classList.remove('swiper-initialized');
    }


} as SwiperDirective<Element> ;